import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BookOutlined } from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';

import { parseNegativeNumber } from '../../lib/parse-negative-number';
import { useGetTradingPlatformSessionCurrency } from '../../models/selectors/get-trading-platform-session-currency';
import type { CalendarData } from '../../models/types/transformed-calendar-data';

import cls from './trading-platform-daily-trades-card.module.css';

interface TradingPlatformDailyTradesCardProps {
    data: CalendarData;
    onClick: (data: CalendarData) => void;
}

export const TradingPlatformDailyTradesCard = memo(
    (props: TradingPlatformDailyTradesCardProps) => {
        const { data, onClick } = props;

        const { t } = useTranslation('trading-journal');

        const currency = useGetTradingPlatformSessionCurrency();

        const isWin = useMemo(
            () => data.total_profit >= 0,
            [data.total_profit],
        );
        const dailyValue = useMemo(
            () => parseNegativeNumber(data.total_profit, currency),
            [currency, data.total_profit],
        );

        const onClickHandler = useCallback(() => {
            onClick(data);
        }, [data, onClick]);

        return (
            <Flex
                align="center"
                className={cls.root}
                onClick={onClickHandler}
            >
                <Flex
                    className={cls.date}
                    vertical
                >
                    <Typography.Text className={cls.text}>
                        {dayjs(data.date).format('dd')}
                    </Typography.Text>
                    <Typography.Text className={cls.text}>
                        {dayjs(data.date).format('DD')}
                    </Typography.Text>
                </Flex>
                <Flex
                    align="start"
                    className={cn(cls.container, {
                        [cls.win]: isWin,
                        [cls.loss]: !isWin,
                    })}
                    justify="space-between"
                >
                    <Flex vertical>
                        <Typography.Text
                            className={cn(cls.value, {
                                [cls.win]: isWin,
                                [cls.loss]: !isWin,
                            })}
                        >
                            {dailyValue}
                        </Typography.Text>
                        <Typography.Text className={cls.trades}>
                            {t('tradingJournalTradesCount', {
                                count: data.trades.length,
                            })}
                        </Typography.Text>
                    </Flex>
                    <BookOutlined className={cls.icon} />
                </Flex>
            </Flex>
        );
    },
);

TradingPlatformDailyTradesCard.displayName = 'TradingPlatformDailyTradesCard';
